(() => {
  const definitions = [
    { id: 'navigation-mobile', state: 'menu_open', trigger: '.mobileMenu-toggle', close: '[data-menu-close-sidebar]' },
    { id: 'search-form-mobile', state: 'open_search_mobile', trigger: '[data-search-mobile]', close: '[data-search-close-sidebar]', initial: 'input[type="search"]' },
    { id: 'halo-cart-sidebar', state: 'cart-sidebar-show', trigger: '[data-cart-sidebar]', close: '[data-close-cart-sidebar]' }
  ];
  const focusSelector = 'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]';
  let lastTrigger = null;

  function usable(element) {
    if (!element || element.disabled || element.closest('[inert]') || element.tabIndex < 0) return false;
    const style = getComputedStyle(element);
    return element.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  }

  function focusInside(drawer) {
    const preferred = drawer.initial && drawer.element.querySelector(drawer.initial);
    const target = usable(preferred) ? preferred : [...drawer.element.querySelectorAll(focusSelector)].find(usable);
    (target || drawer.element).focus({ preventScroll: true });
  }

  function focusOnOpen(drawer, attempt = 0) {
    if (!document.body.classList.contains(drawer.state) || drawer.element.contains(document.activeElement)) return;
    if (document.activeElement !== drawer.previousFocus && document.activeElement !== document.body) return;
    focusInside(drawer);
    if (!drawer.element.contains(document.activeElement) && attempt < 10) {
      window.setTimeout(() => focusOnOpen(drawer, attempt + 1), 50);
    }
  }

  const drawers = definitions.flatMap(definition => {
    const element = document.getElementById(definition.id);
    if (!element) return [];
    element.tabIndex = -1;
    const title = element.querySelector('.halo-sidebar-header .title');
    const trigger = document.querySelector(definition.trigger);
    const label = element.getAttribute('aria-label') || title?.textContent.trim() || trigger?.getAttribute('aria-label');
    if (label) {
      element.setAttribute('role', 'dialog');
      element.setAttribute('aria-label', label);
    }
    return [{ ...definition, element, open: false, previousFocus: null }];
  });

  function synchronize() {
    for (const drawer of drawers) {
      const open = document.body.classList.contains(drawer.state);
      if (open !== drawer.open) {
        if (open) {
          drawer.previousFocus = lastTrigger?.isConnected && lastTrigger.matches(drawer.trigger) ? lastTrigger : document.activeElement;
        } else if (drawer.element.contains(document.activeElement) || document.activeElement === document.body) {
          const fallback = [...document.querySelectorAll(drawer.trigger)].find(usable);
          const target = usable(drawer.previousFocus) ? drawer.previousFocus : fallback;
          target?.focus({ preventScroll: true });
        }
      }
      drawer.element.inert = !open;
      drawer.element.setAttribute('aria-hidden', String(!open));
      if (drawer.element.getAttribute('role') === 'dialog') drawer.element.setAttribute('aria-modal', String(open));
      document.querySelectorAll(drawer.trigger).forEach(trigger => {
        trigger.setAttribute('aria-controls', drawer.id);
        trigger.setAttribute('aria-expanded', String(open));
      });
      if (open && !drawer.open) requestAnimationFrame(() => {
        focusOnOpen(drawer);
      });
      drawer.open = open;
    }
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(definitions.map(drawer => drawer.trigger).join(','));
    if (trigger) lastTrigger = trigger;
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key !== 'Tab' && event.key !== 'Escape') return;
    const drawer = [...drawers].reverse().find(item => item.open);
    if (!drawer) return;
    if (['quickshop-popup-show', 'quick_shop_popup_mobile', 'edit-cart-show', 'term-condition-show', 'has-warning'].some(state => document.body.classList.contains(state))) return;
    if (!drawer.element.contains(event.target) && event.target.closest('.halo-popup, .shopify-pc__banner, .shopify-pc__prefs__dialog, [data-gd-consent-banner]')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      drawer.element.querySelector(drawer.close)?.click();
      return;
    }
    const focusable = [...drawer.element.querySelectorAll(focusSelector)].filter(usable);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
      drawer.element.focus({ preventScroll: true });
    } else if (event.shiftKey && (document.activeElement === first || !drawer.element.contains(document.activeElement))) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && (document.activeElement === last || !drawer.element.contains(document.activeElement))) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  });

  new MutationObserver(synchronize).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  synchronize();
})();


(() => {
  const loader = document.querySelector('script[data-gd-tag-manager]');
  if (!loader || loader.dataset.gdDesignMode === 'true' || window.GDThemeTrackingInitialized) return;
  window.GDThemeTrackingInitialized = true;
  const container = loader.dataset.gdTagManager;
  let inserted = false;
  let lastState = '';

  function applyPrivacy(privacy) {
    if (!privacy) return;
    let analytics = false;
    let marketing = false;
    let preferences = false;
    let sharing = false;
    try {
      analytics = privacy.analyticsProcessingAllowed() === true;
      marketing = privacy.marketingAllowed() === true;
      preferences = privacy.preferencesProcessingAllowed() === true;
      sharing = privacy.saleOfDataAllowed() === true;
    } catch (_) { return; }
    const grant = allowed => allowed ? 'granted' : 'denied';
    const state = {
      analytics_storage: grant(analytics),
      ad_storage: grant(marketing),
      ad_user_data: grant(marketing && sharing),
      ad_personalization: grant(marketing && sharing),
      functionality_storage: grant(preferences),
      personalization_storage: grant(preferences),
      security_storage: 'granted'
    };
    const stateKey = JSON.stringify(state);
    if (lastState !== stateKey) {
      window.gtag?.('consent', 'update', state);
      lastState = stateKey;
    }
    if (!analytics || inserted) return;
    const existing = window.google_tag_manager?.[container] || [...document.scripts].some(script => {
      if (!script.src) return false;
      try {
        const url = new URL(script.src, location.href);
        return url.hostname === 'www.googletagmanager.com' && url.pathname === '/gtm.js' && url.searchParams.get('id') === container;
      } catch (_) { return false; }
    });
    inserted = true;
    if (existing) return;
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(container);
    document.head.appendChild(script);
  }

  function synchronizePrivacy() {
    window.GDPrivacy?.ready().then(applyPrivacy);
  }
  document.addEventListener('visitorConsentCollected', synchronizePrivacy);
  window.addEventListener('pageshow', synchronizePrivacy);
  synchronizePrivacy();
})();

(() => {
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduceMotion() {
    if (!preference.matches) return;
    document.querySelectorAll('video[autoplay]').forEach(video => {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
      video.controls = true;
    });
    if (window.jQuery?.fn?.slick) {
      window.jQuery('.slick-initialized').each(function () {
        window.jQuery(this).slick('slickPause');
      });
    }
  }
  preference.addEventListener('change', reduceMotion);
  document.addEventListener('shopify:section:load', reduceMotion);
  window.addEventListener('load', reduceMotion, { once: true });
  reduceMotion();
})();

(() => {
  let timer;
  function expirePromotions() {
    window.clearTimeout(timer);
    let nextCheck = Infinity;
    const now = Date.now();
    document.querySelectorAll('[data-gdo-campaign-end], .gd-best-of-ribbon, .gd-best-of-feature').forEach(element => {
      const end = Date.parse(element.dataset.gdoCampaignEnd || '2026-09-10T00:00:00-05:00');
      if (!Number.isFinite(end) || now >= end) {
        element.hidden = true;
        element.style.setProperty('display', 'none', 'important');
      } else {
        nextCheck = Math.min(nextCheck, end - now);
      }
    });
    if (Number.isFinite(nextCheck)) timer = window.setTimeout(expirePromotions, Math.min(nextCheck, 2147483647));
  }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) expirePromotions(); });
  document.addEventListener('shopify:section:load', expirePromotions);
  window.addEventListener('pageshow', expirePromotions);
  expirePromotions();
})();

